import React, { useEffect, useRef, useState } from "react"
import {
  Alert,
  Card,
  Col,
  DatePicker,
  Form,
  InputNumber,
  message,
  Row,
  Select,
  Spin,
  Tooltip,
} from "antd"
import dayjs from "dayjs"
import StageStatusForm from "Management/components/StageStatus"
import NoticeV2Btn from "../components/NoticeV2"
import { dateSuffixIcon } from "../components/Notice/Step/options"
import * as com from "../../Common"
import {
  fetchLoanMetaV2,
  patchLoanMetaV2,
  shouldShowDispositionFields,
} from "./api"
import "./OverviewMetaV2.css"

function parseDate(value) {
  if (!value) return null
  if (dayjs.isDayjs(value)) return value
  const d = dayjs(value, "MM/DD/YYYY")
  return d.isValid() ? d : null
}

function coercePatchValue(key, value) {
  if (key === "finalLoanAmount" || key === "totalBrokerCheck") {
    if (value === null || value === undefined || value === "") return 0
    const n = Number(value)
    return Number.isFinite(n) ? n : 0
  }
  return value
}

/**
 * Loan Summary Overview meta card (stage/status + disposition fields).
 * Fed only by /los/v2/loans/{id}/meta — does not read Redux loanOverview JSONB.
 */
export default function OverviewMetaV2({ loanId, onReadyChange }) {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")
  const [meta, setMeta] = useState(null)
  const patchSeq = useRef(0)

  useEffect(() => {
    let cancelled = false
    if (!loanId) {
      setLoading(false)
      setError("missing loan id")
      return undefined
    }
    setLoading(true)
    setError("")
    const token = sessionStorage.getItem("ZeitroA")
    fetchLoanMetaV2(loanId, token)
      .then((js) => {
        if (!cancelled) {
          setMeta(js)
          setLoading(false)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || "Failed to load loan meta")
          setLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [loanId])

  useEffect(() => {
    if (typeof onReadyChange === "function") {
      onReadyChange(!loading)
    }
  }, [loading, onReadyChange])

  const persistPatch = async (patch) => {
    const token = sessionStorage.getItem("ZeitroA")
    const seq = ++patchSeq.current
    setSaving(true)
    try {
      const js = await patchLoanMetaV2(loanId, patch, token)
      // Ignore stale responses when the user edits multiple fields quickly.
      if (seq === patchSeq.current) {
        setMeta(js)
      }
    } catch (err) {
      message.error(err.message || "Failed to save")
      if (seq === patchSeq.current) {
        try {
          setMeta(await fetchLoanMetaV2(loanId, token))
        } catch (_) {
          /* keep prior local state */
        }
      }
    } finally {
      if (seq === patchSeq.current) {
        setSaving(false)
      }
    }
  }

  const applyField = (key, value) => {
    const nextValue = coercePatchValue(key, value)
    setMeta((prev) => (prev ? { ...prev, [key]: nextValue } : prev))
    void persistPatch({ [key]: nextValue })
  }

  const showFields =
    meta &&
    shouldShowDispositionFields(meta.currentStage, meta.currentStatus)

  return (
    <div className="overview-meta-v2 mt-3 text-wrap">
      <Card type="inner" title="Overview">
        {loading ? (
          <div className="overview-meta-v2-loading">
            <Spin size="small" />
          </div>
        ) : error ? (
          <Alert type="warning" message={error} showIcon />
        ) : (
          <>
            <Row>
              <Col xl={18} xs={24} md={18} className="mb-4">
                <div className="formTitle">Status</div>
                <StageStatusForm
                  currentStage={meta.currentStage}
                  currentStatus={meta.currentStatus}
                  loanID={loanId}
                  stageFormItemProps={{ noStyle: true }}
                  statusFormItemProps={{ noStyle: true }}
                  stageProps={{
                    colNum: 2,
                    // Keep Redux in sync for header/other pages; display still comes from V2 meta.
                    isDispatch: true,
                    onChange: (stage) =>
                      setMeta((prev) =>
                        prev ? { ...prev, currentStage: stage } : prev
                      ),
                  }}
                  statusProps={{
                    colNum: 6,
                    isDispatch: true,
                    loanPurpose: meta.purpose,
                    onChange: (status) =>
                      setMeta((prev) =>
                        prev ? { ...prev, currentStatus: status } : prev
                      ),
                  }}
                  // Empty rowData forces modal to load full overview via API (avoids partial wipe).
                  isChangeRedux={false}
                  rowData={{}}
                  successFn={() => {
                    const token = sessionStorage.getItem("ZeitroA")
                    fetchLoanMetaV2(loanId, token)
                      .then(setMeta)
                      .catch(() => {})
                  }}
                />
                <div className="mt-2">
                  <NoticeV2Btn loanId={loanId} />
                </div>
              </Col>
            </Row>

            {showFields && (
              <Row gutter={42}>
                <Col md={24}>
                  <MetaFieldsForm
                    meta={meta}
                    saving={saving}
                    onChange={applyField}
                  />
                </Col>
              </Row>
            )}
          </>
        )}
      </Card>
    </div>
  )
}

function MetaFieldsForm({ meta, saving, onChange }) {
  return (
    <Row gutter={42}>
      <Col md={12} xl={8} xs={24}>
        <Form.Item
          label="Submission date"
          labelCol={{ span: 24 }}
          className="overview-meta-v2-form-item"
        >
          <DatePicker
            size="large"
            format="MM/DD/YYYY"
            disabled={saving}
            value={parseDate(meta.submittedDate)}
            onChange={(e) =>
              onChange("submittedDate", e ? dayjs(e).format("MM/DD/YYYY") : "")
            }
            suffixIcon={dateSuffixIcon}
            style={{ width: "100%" }}
          />
        </Form.Item>
      </Col>
      <Col md={12} xl={8} xs={24}>
        <Form.Item
          label="Disposition date"
          labelCol={{ span: 24 }}
          className="overview-meta-v2-form-item"
        >
          <DatePicker
            size="large"
            format="MM/DD/YYYY"
            disabled={saving}
            value={parseDate(meta.dateClosed)}
            onChange={(e) =>
              onChange("dateClosed", e ? dayjs(e).format("MM/DD/YYYY") : "")
            }
            suffixIcon={dateSuffixIcon}
            style={{ width: "100%" }}
          />
        </Form.Item>
      </Col>
      <Col md={12} xl={8} xs={24}>
        <Form.Item
          label="QM Type"
          labelCol={{ span: 24 }}
          className="overview-meta-v2-form-item"
        >
          <Select
            size="large"
            disabled={saving}
            value={meta.qmType || undefined}
            options={[
              { label: "QM", value: "qm" },
              { label: "Non-QM", value: "non_qm" },
              { label: "Not subject toQM", value: "notqm" },
            ]}
            onChange={(e) => onChange("qmType", e)}
          />
        </Form.Item>
      </Col>
      <Col md={12} xl={8} xs={24}>
        <Form.Item
          label={
            <div className="d-flex align-items-center">
              Lien status
              <Tooltip title="Change will be applied to 1003">
                <img
                  className="ml-2"
                  src="/images/info-tooltip-icon.svg"
                  alt="info-tooltip"
                />
              </Tooltip>
            </div>
          }
          labelCol={{ span: 24 }}
          className="overview-meta-v2-form-item"
        >
          <Select
            size="large"
            disabled={saving}
            value={meta.lienStatus || undefined}
            options={[
              { label: "First lien", value: "firstlien" },
              { label: "Subordinate lien", value: "subordinatelien" },
              { label: "No lien", value: "nolien" },
            ]}
            onChange={(e) => onChange("lienStatus", e)}
          />
        </Form.Item>
      </Col>
      <Col md={12} xl={8} xs={24}>
        <Form.Item
          label={
            <div className="d-flex align-items-center">
              Loan type
              <Tooltip title="Change will be applied to 1003">
                <img
                  className="ml-2"
                  src="/images/info-tooltip-icon.svg"
                  alt="info-tooltip"
                />
              </Tooltip>
            </div>
          }
          labelCol={{ span: 24 }}
          className="overview-meta-v2-form-item"
        >
          <Select
            size="large"
            disabled={saving}
            value={meta.loanType || undefined}
            options={[
              { label: "Conventional", value: "conventional" },
              { label: "FHA", value: "fha" },
              { label: "VA", value: "va" },
              { label: "USDA", value: "usda" },
              { label: "Other", value: "other" },
            ]}
            onChange={(e) => onChange("loanType", e)}
          />
        </Form.Item>
      </Col>
      <Col md={12} xl={8} xs={24}>
        <Form.Item
          label="Source"
          labelCol={{ span: 24 }}
          className="overview-meta-v2-form-item"
        >
          <Select
            size="large"
            disabled={saving}
            value={meta.source || undefined}
            options={[
              { label: "Brokered", value: "brokered" },
              { label: "Retail", value: "retail" },
            ]}
            onChange={(e) => onChange("source", e)}
          />
        </Form.Item>
      </Col>
      <Col md={12} xl={8} xs={24}>
        <Form.Item
          label="HOEPA"
          labelCol={{ span: 24 }}
          className="overview-meta-v2-form-item"
        >
          <Select
            size="large"
            disabled={saving}
            value={typeof meta.hoepa === "boolean" ? meta.hoepa : undefined}
            options={[
              { label: "Yes", value: true },
              { label: "No", value: false },
            ]}
            onChange={(e) => onChange("hoepa", e)}
          />
        </Form.Item>
      </Col>
      <Col md={12} xl={8} xs={24}>
        <Form.Item
          label="Loan amount"
          labelCol={{ span: 24 }}
          className="overview-meta-v2-form-item"
        >
          <div className="overview-meta-v2-loan-amount">
            $ {com.commaize(meta.loanSize)}
          </div>
        </Form.Item>
      </Col>
      {meta.currentStatus === "LoanFunded" && (
        <>
          <Col md={12} xl={8} xs={24}>
            <Form.Item
              label="Final loan amount (if different)"
              labelCol={{ span: 24 }}
              className="overview-meta-v2-form-item"
            >
              <InputNumber
                size="large"
                disabled={saving}
                style={{ width: "100%" }}
                controls={false}
                value={meta.finalLoanAmount || null}
                formatter={(value) =>
                  `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
                parser={(value) =>
                  value == null ? undefined : value.replace(/\$\s?|(,*)/g, "")
                }
                onChange={(e) => onChange("finalLoanAmount", e ?? 0)}
              />
            </Form.Item>
          </Col>
          <Col md={12} xl={8} xs={24}>
            <Form.Item
              label="Total broker check"
              labelCol={{ span: 24 }}
              className="overview-meta-v2-form-item"
            >
              <InputNumber
                size="large"
                disabled={saving}
                style={{ width: "100%" }}
                controls={false}
                value={meta.totalBrokerCheck || null}
                formatter={(value) =>
                  `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
                parser={(value) =>
                  value == null ? undefined : value.replace(/\$\s?|(,*)/g, "")
                }
                onChange={(e) => onChange("totalBrokerCheck", e ?? 0)}
              />
            </Form.Item>
          </Col>
        </>
      )}
    </Row>
  )
}
